import React, { useState, useEffect, memo } from 'react';

// Types
import { ReactionType } from './types';

// Hooks
import { useTooltip } from '../tooltip/useTooltip';

// Components
import { WpPopover, WpPopoverToggle, WpPopoverContent } from '../popover';
import { WpButton, WpButtonGroup } from '../button';
import WpIcon from '../icon/WpIcon';
import QuickReactionButton from './QuickReactionButton';
import WpTooltipOverlay from '../tooltip/WpTooltipOverlay';

// Styles
import './WpReactions.scss';

type Props = {
  reactions: ReactionType[];
  currentUserId: number;
  quickEmojis?: Array<string>;
  onAddReaction?: Function;
  onRemoveReaction?: Function;
  users?: any;
};

const WpReactions = ({
  reactions = [],
  currentUserId,
  onAddReaction,
  onRemoveReaction,
  users,
  quickEmojis = [
    'white_check_mark',
    'thumbsup',
    'thumbsdown',
    'smile',
    'face_palm',
    'heavy_plus_sign',
    'heart',
    'rocket',
    'eyes',
  ],
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [_reactions, setReactions] = useState<ReactionType[]>([]);
  const tooltip = useTooltip();

  const uniqeDataEmojis = [...new Set(_reactions.map(r => r.data))];

  const handleOnClickEmoji = ({ emoji }: { emoji: string }) => {
    const reactions = _reactions.filter(r => r.data === emoji);
    const currentUserReaction = reactions.find(r => +r.userId === +currentUserId);
    const hasCurrentUserAlreadyReacted = Boolean(currentUserReaction);

    if (hasCurrentUserAlreadyReacted) {
      setReactions(_reactions.filter(r => r.slug !== currentUserReaction.slug));
      onRemoveReaction && onRemoveReaction(currentUserReaction);
    } else {
      setReactions([
        ..._reactions,
        {
          data: emoji,
          userId: currentUserId,
          slug: `temp_${emoji}_${currentUserId}`,
          createdAt: new Date().toISOString(),
        },
      ]);
      onAddReaction && onAddReaction(emoji);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    setReactions(
      reactions.sort((a, b) => {
        const aTime = a.createdAt;
        const bTime = b.createdAt;
        return aTime > bTime ? 1 : aTime < bTime ? -1 : 0;
      })
    );
  }, [reactions]);

  const baseClassName = 'WpReactions';
  return (
    <div className={baseClassName}>
      <WpPopover open={isOpen} onOpenChange={setIsOpen} event="click" showArrow={false} placement="top-start">
        <WpPopoverToggle>
          <div className={`${baseClassName}__button`}>
            <WpButton color="gray" size="sm" rounded ref={tooltip.targetRef}>
              <WpIcon name="smile" />
            </WpButton>
            {!isOpen && <WpTooltipOverlay context={tooltip}>Share some love with an emoji</WpTooltipOverlay>}
          </div>
        </WpPopoverToggle>
        <WpPopoverContent>
          <div className={`${baseClassName}__content`}>
            <WpButtonGroup spacing="sm">
              {quickEmojis.map(emoji => (
                <QuickReactionButton
                  emoji={emoji}
                  _reactions={_reactions}
                  currentUserId={currentUserId}
                  showNumber={false}
                  onClick={handleOnClickEmoji}
                  isPicker
                  key={`emoji-picker-list-${emoji}`}
                />
              ))}
            </WpButtonGroup>
          </div>
        </WpPopoverContent>
      </WpPopover>
      <div className={`${baseClassName}__reaction-list`}>
        {uniqeDataEmojis.map(emoji => {
          return (
            <QuickReactionButton
              emoji={emoji}
              _reactions={_reactions}
              currentUserId={currentUserId}
              onClick={handleOnClickEmoji}
              users={users}
              showNumber
              key={`emoji-list-${emoji}`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default memo(WpReactions);
